<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2 text-muted">กำลังโหลดข้อมูล...</p>
    </div>

    <div v-else>
      <div class="card card-custom gutter-b shadow-none border">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="card-title text-dark mb-0">ประวัติระดับสมาชิก</h5>
          </div>

          <div class="table-responsive">
            <b-table
              :items="items"
              :fields="fields"
              head-variant="light"
              hover
              show-empty
              empty-text="ไม่พบข้อมูลประวัติระดับสมาชิก"
            >
              <template #cell(index)="data">
                {{ (page - 1) * perPage + data.index + 1 }}
              </template>

              <template #cell(created_at)="data">
                <span class="text-muted font-size-sm">
                  {{ formatDateTime(data.item.created_at) }}
                </span>
              </template>

              <template #cell(previous_tier)="data">
                <span
                  v-if="
                    data.item.previous_tier &&
                    data.item.previous_tier.name &&
                    data.item.previous_tier.name !== '—'
                  "
                  class="badge font-weight-bold px-3 py-2"
                  :style="{
                    backgroundColor:
                      (data.item.previous_tier.color || '#9E9E9E') + '20',
                    color: data.item.previous_tier.color || '#9E9E9E',
                    border: `1px solid ${
                      data.item.previous_tier.color || '#9E9E9E'
                    }40`
                  }"
                >
                  {{ data.item.previous_tier.name }}
                </span>
                <span v-else class="text-muted font-italic"> — </span>
              </template>

              <template #cell(new_tier)="data">
                <span
                  v-if="data.item.new_tier && data.item.new_tier.name"
                  class="badge font-weight-bold px-3 py-2"
                  :style="{
                    backgroundColor:
                      (data.item.new_tier.color || '#3699FF') + '20',
                    color: data.item.new_tier.color || '#3699FF',
                    border: `1px solid ${
                      data.item.new_tier.color || '#3699FF'
                    }40`
                  }"
                >
                  {{ data.item.new_tier.name }}
                </span>
                <span v-else class="text-muted font-italic"> — </span>
              </template>

              <template #cell(trigger_type)="data">
                <span
                  class="badge badge-light-secondary font-weight-bold px-3 py-2"
                >
                  {{
                    data.item.trigger_name ||
                    data.item.trigger_type ||
                    "ปรับระดับสมาชิก"
                  }}
                </span>
              </template>

              <template #cell(points)="data">
                <span class="font-weight-bolder text-primary">
                  {{ Number(data.item.lifetime_point || 0).toLocaleString() }}
                </span>
                <span class="text-muted font-size-xs ml-1">แต้ม</span>
              </template>

              <template #cell(threshold)="data">
                <span v-if="data.item.threshold" class="text-dark font-size-sm">
                  {{ Number(data.item.threshold.min_point || 0).toLocaleString() }}
                  –
                  {{
                    data.item.threshold.max_point != null
                      ? Number(data.item.threshold.max_point).toLocaleString()
                      : "ขึ้นไป"
                  }}
                </span>
                <span v-else class="text-muted font-italic"> — </span>
              </template>

              <template #cell(campaign)="data">
                <span v-if="data.item.campaign" class="text-dark font-size-sm">
                  {{ data.item.campaign.name }}
                </span>
                <span v-else class="text-muted font-italic"> — </span>
              </template>
            </b-table>
          </div>

          <div
            v-if="totalRows > perPage"
            class="d-flex justify-content-between align-items-center flex-wrap mt-3"
          >
            <span class="text-muted font-size-sm">
              แสดง {{ paginationFrom }} ถึง {{ paginationTo }} จากทั้งหมด
              {{ totalRows }} รายการ
            </span>
            <b-pagination
              v-model="page"
              :total-rows="totalRows"
              :per-page="perPage"
              align="right"
              class="my-0"
              @change="onPageChange"
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";

export default {
  name: "CampaignMemberTierHistory",
  props: {
    embed: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    customerCampaignId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      items: [],
      page: 1,
      perPage: 10,
      totalRows: 0,
      fields: [
        {
          key: "index",
          label: "#",
          class: "text-center align-middle",
          thStyle: { width: "60px" }
        },
        { key: "created_at", label: "วันที่ปรับระดับ", class: "align-middle" },
        { key: "previous_tier", label: "ระดับเดิม", class: "align-middle" },
        { key: "new_tier", label: "ระดับใหม่", class: "align-middle" },
        { key: "trigger_type", label: "สาเหตุ", class: "align-middle" },
        {
          key: "points",
          label: "แต้มสะสม ณ ขณะนั้น",
          class: "align-middle"
        },
        {
          key: "threshold",
          label: "เกณฑ์ระดับใหม่ ณ ขณะนั้น",
          class: "align-middle"
        },
        { key: "campaign", label: "Campaign", class: "align-middle" }
      ]
    };
  },
  computed: {
    resolvedCampaignId() {
      if (this.embed && this.campaignId != null && this.campaignId !== "") {
        return this.campaignId;
      }
      return this.$route.params.campaignId;
    },
    resolvedCustomerCampaignId() {
      if (
        this.embed &&
        this.customerCampaignId != null &&
        this.customerCampaignId !== ""
      ) {
        return this.customerCampaignId;
      }
      return this.$route.params.customerCampaignId;
    },
    paginationFrom() {
      if (this.totalRows === 0) return 0;
      return (this.page - 1) * this.perPage + 1;
    },
    paginationTo() {
      const to = this.page * this.perPage;
      return to > this.totalRows ? this.totalRows : to;
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    onPageChange(nextPage) {
      this.page = nextPage;
      this.$nextTick(() => {
        this.fetchData();
      });
    },
    async fetchData() {
      if (!this.resolvedCampaignId || !this.resolvedCustomerCampaignId) {
        return;
      }

      this.loading = true;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.resolvedCampaignId}/members/${this.resolvedCustomerCampaignId}/tier_history`,
          {
            params: {
              page: String(this.page),
              perPage: String(this.perPage)
            }
          }
        );

        if (data && data.status === "success" && data.data) {
          this.items = Array.isArray(data.data.data) ? data.data.data : [];
          this.totalRows = Number(data.data.total) || 0;
        } else {
          this.items = [];
          this.totalRows = 0;
        }
      } catch (error) {
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(v) {
      if (!v) return "—";
      try {
        const d = new Date(v);
        if (isNaN(d.getTime())) return v;
        return d.toLocaleString("th-TH");
      } catch (e) {
        return v;
      }
    }
  }
};
</script>
